// Publication (1:1) editor — HA public exposure intent. 404-aware create/edit
// like FirewallPolicyEditor; render-critical fields (hostname/mode/tls_owner)
// are locally required so a saved record is actually render-usable.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { publication } from "../../api/endpoints/siteAccess";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import {
  PUBLICATION_MODES,
  TLS_OWNERS,
  type PublicationConfig,
  type PublicationIn,
  type PublicationMode,
  type TlsOwner,
} from "../../api/types/siteAccess";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { SelectField } from "../../components/forms/SelectField";
import { StringListField } from "../../components/forms/StringListField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { parseVendorExtras } from "../../lib/fieldValidators";

const DEFAULTS: PublicationIn = {
  ha_public_hostname: "",
  publication_mode: "option_a_netbird_reverse_proxy",
  tls_owner: "khired_infra",
  published_internal_host: "",
  vendor_extras: {},
  notes: [],
};

export function PublicationEditor({ siteSlug, canWrite }: { siteSlug: string; canWrite: boolean }) {
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    retry: false,
    queryKey: qk.sitePublication(siteSlug),
    queryFn: () => publication.get(siteSlug).then((r) => r.data),
  });

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading publication…</p>;
  const is404 = q.error instanceof ApiError && q.error.status === 404;
  if (q.error && !is404) return <ErrorBanner error={q.error} title="Failed to load publication" />;

  if (q.data) {
    return <PublicationForm key={`existing-${siteSlug}`} siteSlug={siteSlug} canWrite={canWrite} initial={toIn(q.data)} existing />;
  }
  if (creating) {
    return <PublicationForm key={`new-${siteSlug}`} siteSlug={siteSlug} canWrite={canWrite} initial={DEFAULTS} onCancel={() => setCreating(false)} />;
  }
  return (
    <EmptyState
      title="No publication configured"
      description="How this site's Home Assistant is published (hostname, TLS owner, mode)."
      action={canWrite ? <button type="button" style={primaryBtn} onClick={() => setCreating(true)}>Configure publication</button> : null}
    />
  );
}

interface PubDraft {
  ha_public_hostname: string;
  publication_mode: PublicationMode;
  tls_owner: TlsOwner;
  published_internal_host: string;
  vendor_extras_json: string;
  notes: string[];
}

function PublicationForm({
  siteSlug, canWrite, initial, existing, onCancel,
}: {
  siteSlug: string; canWrite: boolean; initial: PublicationIn; existing?: boolean; onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PubDraft>(() => toDraft(initial));
  const set = <K extends keyof PubDraft>(k: K, v: PubDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: PublicationIn) => publication.put(siteSlug, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.sitePublication(siteSlug) }); onCancel?.(); },
  });
  const del = useMutation({
    mutationFn: () => publication.remove(siteSlug),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.sitePublication(siteSlug) }),
  });

  const vendor = parseVendorExtras(draft.vendor_extras_json);
  const isValid =
    draft.ha_public_hostname.trim() !== "" &&
    draft.publication_mode !== undefined &&
    draft.tls_owner !== undefined &&
    vendor.ok;
  const pending = save.isPending || del.isPending;
  const disabled = !canWrite || pending;

  return (
    <FormShell
      submitLabel={existing ? "Save publication" : "Create publication"}
      onSubmit={(e) => { e.preventDefault(); if (!isValid || disabled) return; save.mutate(fromDraft(draft, vendor.ok ? vendor.value : {})); }}
      onCancel={onCancel}
      pending={pending}
      submitDisabled={!canWrite || !isValid}
      error={save.error ?? del.error}
      errorTitle="Publication save failed"
      footer={existing && canWrite ? (
        <div>
          <button type="button" style={dangerBtn} disabled={pending}
            onClick={() => { if (window.confirm(`Delete publication for ${siteSlug}?`)) del.mutate(); }}>
            Delete publication
          </button>
        </div>
      ) : undefined}
    >
      <TextField label="HA public hostname" value={draft.ha_public_hostname}
        onChange={(v) => set("ha_public_hostname", v)} required
        placeholder="fatima-ha.sh.orixen.ai" helper="Public DNS name the HA instance is reachable at." disabled={disabled} />
      <SelectField label="Publication mode" value={draft.publication_mode}
        onChange={(v) => set("publication_mode", v as PublicationMode)} required options={PUBLICATION_MODES} disabled={disabled} />
      <SelectField label="TLS owner" value={draft.tls_owner}
        onChange={(v) => set("tls_owner", v as TlsOwner)} required options={TLS_OWNERS}
        helper="Who holds the TLS cert: Khired infra or the client." disabled={disabled} />
      <TextField label="Published internal host" value={draft.published_internal_host}
        onChange={(v) => set("published_internal_host", v)} placeholder="homeassistant.local:8123" disabled={disabled} />
      <StringListField label="Notes" value={draft.notes} onChange={(v) => set("notes", v)} helper="One per line." disabled={disabled} />
      <details>
        <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--text-dim)" }}>Vendor extras (advanced, JSON)</summary>
        <textarea value={draft.vendor_extras_json} disabled={disabled} rows={5}
          onChange={(e) => set("vendor_extras_json", e.target.value)} placeholder={"{}"}
          style={{ width: "100%", marginTop: 8, padding: "8px 10px", background: "var(--surface-2)",
            border: `1px solid ${vendor.ok ? "var(--border)" : "var(--crit)"}`, borderRadius: 4,
            color: "var(--text)", fontFamily: "monospace", fontSize: "0.85rem", resize: "vertical" }} />
        {!vendor.ok && <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>{vendor.error}</span>}
      </details>
    </FormShell>
  );
}

function toIn(p: PublicationConfig): PublicationIn {
  const { site_id: _site_id, ...rest } = p;
  return rest;
}
function toDraft(p: PublicationIn): PubDraft {
  const hasExtras = Object.keys(p.vendor_extras ?? {}).length > 0;
  return {
    ha_public_hostname: p.ha_public_hostname ?? "",
    publication_mode: p.publication_mode ?? "option_a_netbird_reverse_proxy",
    tls_owner: p.tls_owner ?? "khired_infra",
    published_internal_host: p.published_internal_host ?? "",
    vendor_extras_json: hasExtras ? JSON.stringify(p.vendor_extras, null, 2) : "",
    notes: p.notes ?? [],
  };
}
function fromDraft(d: PubDraft, vendorExtras: Record<string, unknown>): PublicationIn {
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    ha_public_hostname: orNull(d.ha_public_hostname),
    publication_mode: d.publication_mode,
    tls_owner: d.tls_owner,
    published_internal_host: orNull(d.published_internal_host),
    vendor_extras: vendorExtras,
    notes: d.notes,
  };
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  background: "transparent", color: "var(--crit)", border: "1px solid var(--crit)",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", cursor: "pointer",
};
